import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { ErrorMessage, Field, HelperMessage } from '@atlaskit/form';
import { default as AddIcon } from '@atlaskit/icon/glyph/editor/add';
import RemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import SectionMessage from '@atlaskit/section-message';
import Textfield from '@atlaskit/textfield';
import { createAtlassianAccount, fetchAtlassianAccountID } from '@atlassian/temp-proxies';
import { AtlassianEmailUserIdMapping } from '@atlassian/cssxp-types-atlasaccnt';

import isNil from 'lodash/isNil';
import React, { MutableRefObject, useRef, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import { InfoPanel } from '../../info-panel';

interface Props {
  recipients: (string | undefined | null)[];
  updateAdminFieldsDirtyFlag: (flag: boolean) => void;
  isRequired?: boolean;
  placeholder?: string;
  title?: string;
  helperMessage?: string;
  disableSaveButton: (status: boolean) => void;
  allowToAddBillingAdmin: boolean;
}

//nothing
export const UpdateBillingAdminsFields = ({
  recipients,
  updateAdminFieldsDirtyFlag,
  isRequired,
  placeholder,
  title,
  helperMessage,
  disableSaveButton,
  allowToAddBillingAdmin = false,
}: Props) => {
  const billingAdminListOnFile = recipients.filter((recipient) => !isNil(recipient));

  const [adminList, setAdminList] = React.useState(billingAdminListOnFile);

  const [emailAAID, setEmailAAIDList] = useState<AtlassianEmailUserIdMapping>({ email: '', account_id: '' });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<string>('');
  const [disableAddButton, setDisableAddButton] = useState<boolean>(false);
  const [requireAtlassianAccount, setRequireAtlassianAccount] = useState<boolean>(false);
  const [accountSignUpResponseID, setAccountSignUpResponseID] = useState<string>('');
  const [showBillingConfirmation, setShowBillingConfirmation] = useState<boolean>(false);

  // Since setTimeout timer gets lost after component get updated , hence using ref to hold that value.
  const timer: MutableRefObject<number> = useRef(0);
  const debounceDelay = 1000;
  const applicationName = 'wac';
  const updateName = (event: React.FormEvent<HTMLInputElement>, index: number) => {
    const updatedAdminList = [...adminList];
    updatedAdminList[index] = event.currentTarget.value;
    setAdminList(updatedAdminList);
    updateAdminFieldsDirtyFlag(true);
    const methodDebounce = debounce(async (value: string) => validateBillingAdminEmailID(value), debounceDelay);
    methodDebounce(event.currentTarget.value);
  };

  const removeAdmin = (index: number) => {
    const updatedAdminList = [...adminList];
    updatedAdminList.splice(index, 1);
    setAdminList(updatedAdminList);
    setEmailAAIDList({ email: '', account_id: '' });
    setAccountSignUpResponseID('');
    resetFlags();
    setDisableAddButton(false);
    disableSaveButton(false);
    updateAdminFieldsDirtyFlag(true);
  };

  const handleRemoveBillingAdmin = (index: number, billingAdmins: (string | undefined | null)[]) => {
    if (billingAdmins.length === 1) {
      setShowBillingConfirmation(true);
    } else {
      removeAdmin(index);
    }
  };

  const handleRemoveLastBillingAdmin = () => {
    // Removing last billing admin
    removeAdmin(0);
    setShowBillingConfirmation(false);
  };

  const addAdmin = () => {
    setAdminList((adminList) => [...adminList, '']);
    setDisableAddButton(true);
    if (allowToAddBillingAdmin) disableSaveButton(true);
    setEmailAAIDList({ email: '', account_id: '' });
    setAccountSignUpResponseID('');
    updateAdminFieldsDirtyFlag(true);
  };

  const resetFlags = () => {
    setIsError('');
    setRequireAtlassianAccount(false);
    setDisableAddButton(true);
  };

  /**
   * Check if billing admin email is valid .
   * Identity Provider gives AAID linked to email address.
   */
  const validateBillingAdminEmailID = async (email: string) => {
    const payload: { emails: Array<string> } = {
      emails: [email],
    };
    resetFlags();
    if (isEmail(email)) {
      setIsLoading(true);
      try {
        const response = await fetchAtlassianAccountID(payload);
        if (!response.length) {
          setEmailAAIDList({ email, account_id: '' });
          if (allowToAddBillingAdmin) disableSaveButton(true);
          setRequireAtlassianAccount(true);
        } else {
          resetFlags();
          setDisableAddButton(false);
          disableSaveButton(false);
          setEmailAAIDList({ email, account_id: response[0].account_id });
        }
      } catch (error) {
        resetFlags();
        setIsError('Failed to fetch Atlassian Account ID');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const debounce = (fn: Function, timeout: number) => {
    return function (args: string) {
      clearTimeout(timer.current);
      //@ts-ignore
      timer.current = setTimeout(() => fn.call(this, args), timeout);
    };
  };

  const createAtlassianAccountWithEmail = async (admin: string) => {
    // resetFlags();
    try {
      setIsLoading(true);
      const response = await createAtlassianAccount(admin, applicationName);
      resetFlags();
      setDisableAddButton(false);
      disableSaveButton(false);
      setEmailAAIDList({ email: admin, account_id: response.id });
      setAccountSignUpResponseID(response.id);
    } catch (error) {
      setIsError(`Error in creating an Atlassian Account for ${admin}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getAccountSignUpBanner = (admin: string) => {
    const needAtlassianAccountMessage = 'No Atlassian Account found for the email contact. Click on Add to create one';
    const createdAtlassianAccountSuccessMessage = `Atlassian account successfully created for ${emailAAID.email}`;
    const bannerMessage =
      'Selecting this option will create an unverified Atlassian Account for the given email contact. The  contact will receive a verification email and will require to complete the sign up process.';
    return (
      <>
        {emailAAID?.email === admin && (
          <div data-testid="account-creation-banner">
            {requireAtlassianAccount ? (
              <>
                <br />
                <InfoPanel type="warning" message={needAtlassianAccountMessage}></InfoPanel>
                <br />
                <SectionMessage title="Create Atlassian Account">
                  <p>{bannerMessage}</p>
                </SectionMessage>
              </>
            ) : (
              <>
                {emailAAID.account_id !== '' && accountSignUpResponseID !== '' && (
                  <>
                    <br />
                    <InfoPanel type="success" message={createdAtlassianAccountSuccessMessage}></InfoPanel>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <h3>{title ?? ''}</h3>
      <HelperMessage>{helperMessage ?? ''}</HelperMessage>
      {showBillingConfirmation && (
        <>
          <br />
          <InfoPanel
            type="warning"
            message={
              <div>
                <div style={{ marginBottom: 12 }}>
                  Transaction account will be deactivated, if the last billing admin has been removed. Do you agree to
                  delete the last billing admin?
                </div>
                <ButtonGroup>
                  <Button appearance="primary" testId="confirm-button" onClick={handleRemoveLastBillingAdmin}>
                    Confirm
                  </Button>
                  <Button appearance="subtle" onClick={() => setShowBillingConfirmation(false)}>
                    Cancel
                  </Button>
                </ButtonGroup>
              </div>
            }
          />
        </>
      )}
      {!adminList.length && (
        <>
          <br />
          <InfoPanel
            type="warning"
            message={'Once saving the changes, Transaction account will be deactivated, as there is not billing admin'}
          />
        </>
      )}
      {adminList.map((admin, index, array) => {
        return (
          <div key={index}>
            <div key={index} className="billing-admins">
              <Field
                name={`recipients[${index}]`}
                defaultValue={admin}
                isRequired={isRequired}
                validate={() => {
                  if (admin) {
                    return isEmail(admin) ? undefined : `${admin} must be a valid e-mail address`;
                  }
                }}
              >
                {({ fieldProps, error }: any) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      testId="invoice-contact"
                      placeholder={placeholder ?? ''}
                      autoComplete="off"
                      isDisabled={index !== adminList.length - 1}
                      onChange={(event: React.FormEvent<HTMLInputElement>) => {
                        updateName(event, index);
                        fieldProps.onChange(event);
                      }}
                      value={admin}
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                    {isError && index === adminList.length - 1 && (
                      <ErrorMessage testId="error-in-fetching">{isError}</ErrorMessage>
                    )}
                  </>
                )}
              </Field>
              <Button
                className="remove-row"
                testId="remove-button"
                onClick={() => handleRemoveBillingAdmin(index, array)}
              >
                <RemoveIcon label="remove" />
              </Button>
              <ButtonGroup>
                {emailAAID?.account_id === '' && admin === emailAAID.email && isEmail(admin) ? (
                  <LoadingButton
                    appearance="default"
                    type="button"
                    isLoading={isLoading}
                    testId="create-account"
                    onClick={() => createAtlassianAccountWithEmail(admin)}
                    isDisabled={!allowToAddBillingAdmin}
                  >
                    {`Add`}
                  </LoadingButton>
                ) : (
                  array.length - 1 === index && (
                    <LoadingButton
                      testId="add-button"
                      onClick={addAdmin}
                      isLoading={isLoading}
                      isDisabled={disableAddButton}
                    >
                      <AddIcon label="add" />
                    </LoadingButton>
                  )
                )}
              </ButtonGroup>
            </div>
            {admin && getAccountSignUpBanner(admin)}
          </div>
        );
      })}
    </>
  );
};

export default UpdateBillingAdminsFields;
